import type { ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';

import type { SearchInputChangeEventValue } from '@interstate/components/SearchInput';
import type { VehicleObjectForSimilaritySearch } from '@makemydeal/dr-platform-types';

import {
    initialFilterList,
    initialAgeOfInventoryFilterGroup,
    AI_PROMPT_MODE,
    AI_PROMPT_PLACEHOLDER,
    AI_REQUEST_TRIGGER
} from '../../constants';
import { changeVehicleCreators, changeVehicleSelectors, middlewareMgr, setAgentPromptAttr } from '../../store';
import type { AppliedFilter, FilterGroup, FilterItemValue, VehicleSearchOptions, SortOption } from '../../types';
import {
    appendDealershipsFilter,
    applyFilterSummary,
    buildVehicleIdentifier,
    getAppliedFilters,
    resetAllFilters,
    resetFilters,
    updateFilter
} from '../../utils';
import { createVehicleSearchFilterEvent } from '../../analytics';
import { dealerSelectors } from '@makemydeal/dr-shared-store';

export interface VehicleSearchFiltersContextProps {
    filterList: FilterGroup[];
    searchValue: string;
    appliedFilters: AppliedFilter[];
    selectedFilters: AppliedFilter[];
    filterSlideOutOpen: boolean;
    isSearchError: boolean;
    setFilterSlideOutOpen: (newValue: boolean) => void;
    updateSearchResults: () => void;
    createSearchOptions: (overrides?: Partial<VehicleSearchOptions>) => VehicleSearchOptions;
    clearAllFilters: () => void;
    clearSortBy: () => void;
    updateFilterValue: (appliedFilter: AppliedFilter, value: FilterItemValue) => void;
    updateSearchValue: (value: SearchInputChangeEventValue) => void;
    removeFilters: (appliedFilters: AppliedFilter[]) => void;
    setSortBy: (sortBy: SortOption) => void;
    sortBy: SortOption;
    currentPage: number;
    pageSize: number;
    setCurrentPage: (page: number) => void;
    forceSmallBreakpoint: boolean;
    /** Override-aware: the deal's committed vehicle unless the host supplied one of its own. */
    currentVehicleForSimilaritySearch?: VehicleObjectForSimilaritySearch;
    existingVehicleId?: string;
    hasUserInteracted: boolean;
}

interface VehicleSearchFiltersProviderProps {
    children: ReactNode;
    forceSmallBreakpoint?: boolean;
    isInsideSlideOut?: boolean;
    /** Seeds the AI similarity search from this vehicle instead of the deal's committed one. */
    similaritySearchVehicleOverride?: VehicleObjectForSimilaritySearch;
}

const VehicleSearchFiltersContext = createContext<VehicleSearchFiltersContextProps | null>(null);

export const useVehicleSearchFiltersContext = () => {
    const context = useContext(VehicleSearchFiltersContext);

    if (!context) {
        throw new Error('VehicleSearchFiltersContext must be used within a VehicleSearchFiltersContextProvider.');
    }

    return context;
};

export const DEFAULT_PAGE_SIZE = 25;
export const COMPACT_PAGE_SIZE = 12;

export const VehicleSearchFiltersContextProvider: React.FC<VehicleSearchFiltersProviderProps> = ({
    children,
    forceSmallBreakpoint,
    isInsideSlideOut,
    similaritySearchVehicleOverride
}) => {
    const dispatch = useDispatch();
    const { getServicesBff, getCommonOrgId, isEnableCrossDealershipAIVehicleSearch } = changeVehicleSelectors.additionalSelectors;

    // AI-related selectors
    const isDealerAISearchEnabled = useSelector(dealerSelectors.isDealerAISearchEnabled);
    const isAgeOfInventorySearchEnabled = useSelector(changeVehicleSelectors.isAgeOfInventorySearchEnabled);
    const committedVehicleForSimilaritySearch = useSelector(changeVehicleSelectors.getCurrentVehicleForSimilaritySearch);
    const committedExistingVehicleId = useSelector(changeVehicleSelectors.getExistingVehicleIdentifier);
    const currentVehicleForSimilaritySearch = similaritySearchVehicleOverride ?? committedVehicleForSimilaritySearch;
    const existingVehicleId = similaritySearchVehicleOverride
        ? buildVehicleIdentifier(similaritySearchVehicleOverride)
        : committedExistingVehicleId;
    const currentAgentPromptString = useSelector(changeVehicleSelectors.getAgentPromptString);

    const summary = useSelector(
        isDealerAISearchEnabled ? changeVehicleSelectors.getAgentFilterSummary : changeVehicleSelectors.getSearchSummary
    );
    const bffEndpoint = useSelector(getServicesBff);
    const orgId = useSelector(getCommonOrgId);
    const dealerships = useSelector(changeVehicleSelectors.getDealerships);

    // Cross-dealership toggle selectors - determine if dealerships filter should show based on mode
    const isCrossDealershipAIVehicleSearchEnabled = useSelector((state) =>
        isEnableCrossDealershipAIVehicleSearch ? isEnableCrossDealershipAIVehicleSearch(state) : false
    );

    // Compute effective dealerships based on current mode and respective toggle
    const effectiveDealerships = useMemo(() => {
        if (isDealerAISearchEnabled) {
            return isCrossDealershipAIVehicleSearchEnabled ? dealerships : undefined;
        }
        return dealerships;
    }, [isDealerAISearchEnabled, isCrossDealershipAIVehicleSearchEnabled, dealerships]);

    const initialSearchParameters = useSelector(changeVehicleSelectors.getInitialSearchParameters);
    const initialPageSize = useSelector(changeVehicleSelectors.getInitialPageSize);
    const filterSummary = useSelector(changeVehicleSelectors.getSearchSummary);
    const agentFilterSummary = useSelector(changeVehicleSelectors.getAgentFilterSummary);
    const vehicleVin = useSelector(changeVehicleSelectors.getVehicleVin);
    const vehicleStockNumber = useSelector(changeVehicleSelectors.getVehicleStockNumber);
    const hasFilterSummary = isDealerAISearchEnabled
        ? agentFilterSummary && Object.keys(agentFilterSummary).length > 0
        : filterSummary && Object.keys(filterSummary).length > 0;

    const [filterList, setFilterList] = useState(() => {
        const dealershipFilterList = appendDealershipsFilter(applyFilterSummary(initialFilterList), effectiveDealerships, orgId);
        const shouldShowAgeOfInventoryFacet = isDealerAISearchEnabled && isAgeOfInventorySearchEnabled;
        return shouldShowAgeOfInventoryFacet ? [...dealershipFilterList, initialAgeOfInventoryFilterGroup] : dealershipFilterList;
    });
    const [searchValue, setSearchValue] = useState(initialSearchParameters?.search || '');
    const [sortBy, setSortBy] = useState<SortOption>(!isDealerAISearchEnabled ? 'listedPriceDesc' : 'default');
    const [filterSlideOutOpen, setFilterSlideOutOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [isFilterSelected, setIsFilterSelected] = useState(false);
    const [isSortBySelected, setIsSortBySelected] = useState(false);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);

    const getEffectivePageSize = () => {
        if (initialPageSize) return initialPageSize;
        if (isInsideSlideOut) return COMPACT_PAGE_SIZE;
        return DEFAULT_PAGE_SIZE;
    };

    const [pageSize] = useState(getEffectivePageSize());
    const hasDispatchedFirstSearchRef = useRef(false);
    const pendingTriggerRef = useRef<AI_REQUEST_TRIGGER | undefined>(undefined);

    const appliedFilters = useMemo(() => getAppliedFilters(filterList), [filterList]);
    const selectedFilters = useMemo(() => appliedFilters.filter((item) => !!item.value), [appliedFilters]);

    const prevAppliedFiltersRef = useRef<AppliedFilter[]>([]);

    const isSearchError = useSelector(
        isDealerAISearchEnabled ? changeVehicleSelectors.getShowAgentFilterError : changeVehicleSelectors.getShowSearchError
    );

    const createSearchOptions = useCallback(
        (overrides?: Partial<VehicleSearchOptions>) => {
            return {
                appliedFilters,
                search: searchValue,
                commonOrgDealerId: orgId,
                page: currentPage,
                pageSize: pageSize,
                ...(sortBy !== 'default' && { sort: sortBy }),
                ...overrides
            };
        },
        [appliedFilters, searchValue, orgId, currentPage, pageSize, sortBy]
    );

    const updateSearchResults = useCallback(() => {
        const searchOptions = createSearchOptions();

        // Forward CDL v3 event to host application
        if (hasDispatchedFirstSearchRef.current) {
            const cdlEvent = createVehicleSearchFilterEvent({
                appliedFilters,
                resultsCount: 0,
                searchText: searchValue,
                aiMode: isDealerAISearchEnabled
            });
            const hostAction = middlewareMgr.hostActions.forwardCDLEvent(cdlEvent.payload);
            dispatch(hostAction);
        }

        if (!isDealerAISearchEnabled || !hasFilterSummary) {
            dispatch(changeVehicleCreators.searchVehiclesWithFilters(searchOptions, bffEndpoint, { defaultOrgId: orgId }));
        }

        if (isDealerAISearchEnabled && hasFilterSummary && (isFilterSelected || isSortBySelected)) {
            // Switch to semantic search when filters are applied, preserving the active prompt
            if (isFilterSelected) {
                dispatch(
                    setAgentPromptAttr(currentAgentPromptString || AI_PROMPT_PLACEHOLDER.SEARCH, AI_PROMPT_MODE.SEMANTIC_SEARCH)
                );
            }
            const trigger = pendingTriggerRef.current;
            pendingTriggerRef.current = undefined;
            dispatch(
                changeVehicleCreators.processVehicleAgentRequestWithFilters({
                    appliedFilters,
                    sortBy,
                    selectedPage: currentPage,
                    vehicle: currentVehicleForSimilaritySearch,
                    trigger
                })
            );
        }

        // Indicate that the first search has been dispatched
        hasDispatchedFirstSearchRef.current = true;
    }, [createSearchOptions, appliedFilters, searchValue, dispatch, bffEndpoint, hasFilterSummary, currentAgentPromptString]);

    // Debounce filter changes to prevent multiple rapid network calls (e.g., when entering min and max price)
    const FILTER_DEBOUNCE_MS = 300;
    const debouncedUpdateSearchResults = useMemo(() => debounce(updateSearchResults, FILTER_DEBOUNCE_MS), [updateSearchResults]);

    // Cleanup: cancel pending debounce on unmount to prevent memory leaks and stale calls
    useEffect(() => {
        return () => {
            debouncedUpdateSearchResults.cancel();
        };
    }, [debouncedUpdateSearchResults]);

    const clearAllFilters = useCallback(() => {
        setHasUserInteracted(true);
        setFilterList(resetAllFilters);
        setCurrentPage(1);
        if (isDealerAISearchEnabled) {
            setIsFilterSelected(false);
            // Switch to semantic search when clearing filters, preserving the active prompt
            dispatch(setAgentPromptAttr(currentAgentPromptString || AI_PROMPT_PLACEHOLDER.SEARCH, AI_PROMPT_MODE.SEMANTIC_SEARCH));
            dispatch(
                changeVehicleCreators.processVehicleAgentRequestWithFilters({
                    appliedFilters: [],
                    sortBy,
                    selectedPage: 1,
                    vehicle: currentVehicleForSimilaritySearch,
                    trigger: AI_REQUEST_TRIGGER.FILTERS_CLEAR
                })
            );
        }
    }, [isDealerAISearchEnabled, dispatch, currentAgentPromptString, sortBy, currentVehicleForSimilaritySearch]);

    const clearSortBy = useCallback(() => {
        if (isDealerAISearchEnabled) {
            setSortBy('default');
            setIsSortBySelected(false);
        }
    }, []);

    const updateFilterValue = useCallback((appliedFilter: AppliedFilter, value: FilterItemValue) => {
        setHasUserInteracted(true);
        pendingTriggerRef.current = AI_REQUEST_TRIGGER.FILTER_CHANGE;
        setFilterList((filters) => updateFilter(appliedFilter, value, filters));
        setCurrentPage(1);
        if (isDealerAISearchEnabled) {
            setIsFilterSelected(true);
        }
    }, []);

    const updateSearchValue = useCallback((value?: SearchInputChangeEventValue) => {
        setHasUserInteracted(true);
        // Store the original value for display, but normalize it for comparison
        pendingTriggerRef.current = AI_REQUEST_TRIGGER.USER_SEARCH;
        const trimmedValue = value?.trim() ?? '';
        // We don't normalize to lowercase here because the search might be case-sensitive
        // The normalization will happen when comparing values
        setSearchValue(trimmedValue);
        setCurrentPage(1);
    }, []);

    const removeFilters = useCallback((appliedFilters: AppliedFilter[]) => {
        setHasUserInteracted(true);
        pendingTriggerRef.current = AI_REQUEST_TRIGGER.FILTER_CHANGE;
        setFilterList((filters) => resetFilters(appliedFilters, filters));
        setCurrentPage(1);
    }, []);

    const handleSetCurrentPage = useCallback((page: number) => {
        setHasUserInteracted(true);
        pendingTriggerRef.current = AI_REQUEST_TRIGGER.PAGINATION;
        setCurrentPage(page);
    }, []);

    const handleSetSortBy = useCallback(
        (newSortBy: SortOption) => {
            setHasUserInteracted(true);
            pendingTriggerRef.current = AI_REQUEST_TRIGGER.SORT_CHANGE;
            setSortBy(newSortBy);
            setCurrentPage(1);

            // Always dispatch search when sorting changes
            const searchOptions = createSearchOptions({
                page: 1, // Reset to page 1 when sorting
                ...(newSortBy !== 'default' && { sort: newSortBy })
            });

            if (isDealerAISearchEnabled) {
                setIsSortBySelected(true);
            } else {
                dispatch(changeVehicleCreators.searchVehiclesWithFilters(searchOptions, bffEndpoint, { defaultOrgId: orgId }));
            }
        },
        [createSearchOptions, dispatch, bffEndpoint, isDealerAISearchEnabled, orgId, vehicleVin, vehicleStockNumber]
    );

    useEffect(() => {
        setFilterList((filters) => applyFilterSummary(filters, summary));
    }, [summary]);

    useEffect(() => {
        setFilterList((filters) => appendDealershipsFilter(filters, effectiveDealerships, orgId));
    }, [effectiveDealerships, orgId]);

    useEffect(() => {
        const appliedFiltersChanged = JSON.stringify(appliedFilters) !== JSON.stringify(prevAppliedFiltersRef.current);
        if (appliedFiltersChanged) {
            prevAppliedFiltersRef.current = appliedFilters;
            // Use debounced version to prevent multiple rapid network calls
            debouncedUpdateSearchResults();
        }
    }, [appliedFilters, debouncedUpdateSearchResults]);

    useEffect(() => {
        if (isSortBySelected && isDealerAISearchEnabled) {
            updateSearchResults();
        }
    }, [isSortBySelected, sortBy]);

    useEffect(() => {
        updateSearchResults();
    }, [currentPage, searchValue]);

    return (
        <VehicleSearchFiltersContext.Provider
            value={{
                filterList,
                appliedFilters,
                selectedFilters,
                searchValue,
                sortBy,
                currentPage,
                pageSize,
                filterSlideOutOpen,
                isSearchError,
                updateSearchResults,
                createSearchOptions,
                setFilterSlideOutOpen,
                clearAllFilters,
                clearSortBy,
                updateFilterValue,
                removeFilters,
                setSortBy: handleSetSortBy,
                setCurrentPage: handleSetCurrentPage,
                updateSearchValue,
                forceSmallBreakpoint: !!forceSmallBreakpoint,
                currentVehicleForSimilaritySearch,
                existingVehicleId,
                hasUserInteracted
            }}
        >
            {children}
        </VehicleSearchFiltersContext.Provider>
    );
};
